// client/src/components/promoters/PromoterPortal.jsx
//
// /:tenant/promoter — the member-portal page a promoter lands on from their
// invite (or after member sign-in). Mobile first: one card per event with
// their personal link, Copy / Share (Web Share API) / QR, their own clicks,
// conversions and earnings, and progress toward each reward.
// Shows only the signed-in promoter's own numbers (the API is scoped to them).
// Growth Phase 2, feature_key "promoters".
//
// Auth: member session (httpOnly memberAuthToken cookie, Bearer fallback for
// older localStorage sessions, same as MyTicketsPage.jsx).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { formatCount, formatMoney, barPct } from '../analytics/attributionFormat';
import '../analytics/PerformancePanel.css';
import './Promoters.css';

const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || '';
const MEMBER_TOKEN_KEY = 'memberToken';

function memberHeaders() {
  try {
    const token = localStorage.getItem(MEMBER_TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function formatDate(s) {
  if (!s) return '';
  try {
    return new Date(String(s).replace(' ', 'T') + (/[Zz]|[+-]\d\d:?\d\d$/.test(s) ? '' : 'Z'))
      .toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(s);
  }
}

export default function PromoterPortal() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | ready | unauth | notPromoter | error
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: resp } = await axios.get(`${BASE_URL}/api/promoters/portal/${tenant}/me`, {
        headers: memberHeaders(), withCredentials: true,
      });
      setData(resp.data);
      setState('ready');
    } catch (err) {
      const s = err?.response?.status;
      setState(s === 401 || s === 403 ? 'unauth' : s === 404 ? 'notPromoter' : 'error');
    }
  }, [tenant]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="pp-page">
      {state === 'loading' && <div className="surface">{t('common.loading', 'Loading...')}</div>}
      {state === 'unauth' && (
        <div className="surface">
          <p>{t('promoterPortal.signIn', 'Sign in to see your promoter links and stats.')}</p>
          <button type="button" className="pp-btn" onClick={() => navigate(`/${tenant}/memberLogin`)}>
            {t('promoterPortal.signInButton', 'Member sign in')}
          </button>
        </div>
      )}
      {state === 'notPromoter' && (
        <div className="note info">{t('promoterPortal.notPromoter', 'This account is not set up as a promoter here. Ask the organizer to invite you.')}</div>
      )}
      {state === 'error' && <div className="note error">{t('promoterPortal.loadError', "Couldn't load your stats. Try again shortly.")}</div>}
      {state === 'ready' && data && <Dashboard data={data} tenant={tenant} />}
    </div>
  );
}

function Dashboard({ data, tenant }) {
  const { t } = useTranslation();
  const { promoter, totals, earnings, assignments, organization } = data;
  const owed = (earnings.pending || 0) + (earnings.approved || 0);
  return (
    <>
      <header className="pp-head">
        <h1>{t('promoterPortal.hello', 'Hi {{name}}', { name: promoter.name })}</h1>
        <p>
          {organization ? `${organization} · ` : ''}
          {t('promoterPortal.yourCode', 'Your code')}: <span className="pp-code">{promoter.code}</span>
        </p>
      </header>

      {promoter.status === 'paused' && (
        <div className="note warn">{t('promoterPortal.paused', 'Your promoter account is paused. Your links still work, but new sales do not earn commission.')}</div>
      )}

      <section className="surface">
        <div className="pp-stats">
          <Stat label={t('performance.clicks', 'Clicks')} value={formatCount(totals.clicks)} />
          <Stat label={t('performance.conversions', 'Conversions')} value={formatCount(totals.conversions)} />
          <Stat label={t('promoterPortal.earned', 'Earned')} value={formatMoney(owed + (earnings.paid || 0))} sub={earnings.currency || ''} />
        </div>
        <p className="surface-muted perf-small">
          {t('promoterPortal.earningsLine', 'Owed {{owed}} · Paid {{paid}}', { owed: formatMoney(owed), paid: formatMoney(earnings.paid) })}
        </p>
      </section>

      {assignments.length === 0 ? (
        <div className="note info">{t('promoterPortal.noEvents', 'You have not been given an event yet. Check back soon.')}</div>
      ) : (
        assignments.map((a) => <EventCard key={a.id} a={a} tenant={tenant} code={promoter.code} />)
      )}
    </>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="perf-kpi">
      <div className="perf-kpi-value">{value}</div>
      <div className="perf-kpi-label">{label}{sub ? ` (${sub})` : ''}</div>
    </div>
  );
}

function EventCard({ a, tenant, code }) {
  const { t } = useTranslation();
  const [notice, setNotice] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const link = a.trackingUrl || a.refUrl;
  const title = a.itemName ? `${a.eventName} · ${a.itemName}` : a.eventName;

  useEffect(() => () => { if (qrUrl) URL.revokeObjectURL(qrUrl); }, [qrUrl]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setNotice({ kind: 'ok', text: t('promoterPortal.copied', 'Link copied.') });
    } catch {
      setNotice({ kind: 'error', text: t('links.copyError', 'Copy failed. Select the link and copy it manually.') });
    }
  };

  const share = async () => {
    if (typeof navigator.share !== 'function') return copy();
    try {
      await navigator.share({ title, text: t('promoterPortal.shareText', 'Get your spot for {{event}}', { event: a.eventName }), url: link });
    } catch (err) {
      if (err?.name !== 'AbortError') setNotice({ kind: 'error', text: t('promoterPortal.shareError', 'Sharing did not work. Copy the link instead.') });
    }
  };

  const toggleQr = async () => {
    if (qrUrl) { URL.revokeObjectURL(qrUrl); setQrUrl(null); return; }
    try {
      const res = await fetch(`${BASE_URL}/api/promoters/portal/${tenant}/assignments/${a.id}/qr`, {
        credentials: 'include', headers: memberHeaders(),
      });
      if (!res.ok) throw new Error(String(res.status));
      setQrUrl(URL.createObjectURL(await res.blob()));
    } catch {
      setNotice({ kind: 'error', text: t('promoterPortal.qrError', 'Could not load the QR code.') });
    }
  };

  const goal = a.goalCount || 0;
  return (
    <section className="surface pp-card">
      <h2>{title}</h2>
      {a.startdate && <p className="pp-sub">{formatDate(a.startdate)}</p>}

      {link && <code className="pp-link">{link}</code>}
      <div className="pp-actions">
        <button type="button" className="pp-btn" onClick={copy} disabled={!link}>{t('links.copy', 'Copy')}</button>
        <button type="button" className="pp-btn" onClick={share} disabled={!link}>{t('promoterPortal.share', 'Share')}</button>
        <button type="button" className="pp-btn secondary" onClick={toggleQr} aria-expanded={!!qrUrl} disabled={!a.trackingUrl}>
          {t('promoterPortal.qr', 'QR code')}
        </button>
      </div>
      {notice && <div className={`note ${notice.kind === 'ok' ? 'ok' : 'error'}`} role="status">{notice.text}</div>}
      {qrUrl && (
        <div className="pp-qr">
          <img src={qrUrl} alt={t('promoterPortal.qrAlt', 'QR code for your link to {{event}}', { event: a.eventName })} />
          <div>
            <a className="lm-btn secondary" href={qrUrl} download={`${code}-${a.id}.png`}>{t('promoterPortal.download', 'Download')}</a>
          </div>
        </div>
      )}
      {a.refUrl && a.trackingUrl && (
        <p className="surface-muted perf-small">
          {t('promoterPortal.refHint', 'You can also add ?ref={{code}} to any page for this event.', { code })}
        </p>
      )}

      <div className="pp-stats">
        <Stat label={t('performance.clicks', 'Clicks')} value={formatCount(a.stats.clicks)} />
        <Stat label={t('performance.uniqueVisitors', 'Unique visitors')} value={formatCount(a.stats.uniqueVisitors)} />
        <Stat label={t('performance.conversions', 'Conversions')} value={formatCount(a.stats.conversions)} />
      </div>

      {goal > 0 && (
        <Progress label={t('promoterPortal.goal', 'Goal')} current={a.stats.conversions} target={goal} />
      )}
      {(a.rewards || []).map((r) => (
        <Progress key={r.id} label={r.text} current={a.stats.conversions} target={r.threshold} reached={r.reached} />
      ))}
    </section>
  );
}

function Progress({ label, current, target, reached }) {
  const { t } = useTranslation();
  const done = reached ?? current >= target;
  return (
    <div className="pp-progress">
      <div className="pp-progress-label">
        <span>{label}</span>
        <span className={done ? 'pp-reached perf-num' : 'perf-num'}>
          {done ? t('promoterPortal.reached', 'Reached') : `${formatCount(Math.min(current, target))} / ${formatCount(target)}`}
        </span>
      </div>
      <div
        className="perf-bar" role="progressbar" aria-label={label}
        aria-valuemin={0} aria-valuemax={target} aria-valuenow={Math.min(current, target)}
      >
        <div className={`perf-bar-fill${done ? ' alt' : ''}`} style={{ width: `${done ? 100 : barPct(current, target)}%` }} />
      </div>
    </div>
  );
}
